import { hasEntitlementCookie } from 'Utils/Entitlements';
import { getSharedProperties, getPageNameTypeData } from 'Utils/Analytics/SharedProperties';
import config from 'Config/analytics.json';
import { collectAmplitudeExperiments } from './Amplitude';
import { getDefaultConfig, getStoreState } from 'Utils/Store';

const { defaultData, events, filter, pages: pagesParams } = config.tealium;
const TEALIUM_EVENTS = events || {};
const TRACK_MAX_RETRIES = 10;
const TRACK_RETRY_WAIT_TIME = 500;
const PAGE_PREFIX = 'analytics_pv_';
const EVENT_PREFIX = 'analytics_ev_';

const getPlatformVersion = () => {
  const { appVersion, buildNumber } = getDefaultConfig();
  return `foxnation accounts web|v${appVersion} (${buildNumber})`;
};

function tealiumViewInit() {
  if (window && window.utag && window.utag.view && window.utag_data) {
    console.info('Tealium: onload');
    console.debug(`Tealium [Page]: ${window.location.pathname}`);
    utag.view(window.utag_data);
    window.initViewComplete = true;
  }
}

const getProfileStartSource = () => {
  const { source } = getStoreState('tracking');
  return source || 'other';
};

const checkArrayFilter = (filterArray, event) => {
  return filterArray.indexOf(event) > -1;
};

const getEntitlementParam = () => {
  const { entitlementId } = getDefaultConfig();
  const entitlementParam = hasEntitlementCookie() ? entitlementId : 'no entitlements';
  return { network_entitlement_list: entitlementParam };
};

const getPageParams = (page) => {
  if (checkArrayFilter(filter.pages, page) && pagesParams[page]) {
    const pageEvent = pagesParams[page];
    const { profileId } = getStoreState('user');

    return {
      platform_version: getPlatformVersion(),
      dcg_profile_id: profileId,
      is_founders: 'false',
      ...pageEvent,
      ...getPageNameTypeData(pageEvent),
    };
  }
  return null;
};

const getDefaultPackageName = (event) =>
  checkArrayFilter(filter.no_plans_selected, event) ? { package_name: 'no package selected' } : {};

const checkFirstLoad = () => window.tealiumFirstLoad;

// default value assignment only works when parameter is `undefined`.
const getParams = (event, data = {}) => {
  const { profileId, mvpdId } = getStoreState('user');
  const { appVersion, buildNumber, entitlementId } = getDefaultConfig();

  let paramsData = data;
  if (paramsData === null) paramsData = {};

  const params = {
    ...defaultData,
    ...config.properties[entitlementId],
    ...config.properties.common,
    app_version: appVersion,
    app_build: buildNumber,
    ...getPageParams(event),
    action: event,
    platform_version: getPlatformVersion(),
    mvpd: mvpdId,
    dcg_profile_id: profileId,
    is_founders: 'false',
    ...getSharedProperties(event, data),
    ...(checkArrayFilter(filter.profile_start_source, event) && { profile_start_source: getProfileStartSource() }),
    ...getDefaultPackageName(event),
    ...paramsData,
  };

  params.user = params.user || {};
  params.user = { ...params.user, ...getEntitlementParam() };

  return params;
};

const setPrefix = (eventName) =>
  checkArrayFilter(filter.pages, eventName) ? `${PAGE_PREFIX}${eventName}` : `${EVENT_PREFIX}${eventName}`;

const CustomEvent = (lclEvent, params) => {
  const lclParams = params || { bubbles: false, cancelable: false, detail: undefined };
  const evt = document.createEvent('CustomEvent');
  evt.initCustomEvent(lclEvent, lclParams.bubbles, lclParams.cancelable, lclParams.detail);
  return evt;
};

const setUtagData = () => {
  const { appVersion, buildNumber, entitlementId } = getDefaultConfig();
  const currUtagData = window.utag_data || {};
  window.utag_data = {
    ...currUtagData,
    ...config.properties[entitlementId],
    ...config.properties.common,
    app_version: appVersion,
    app_build: buildNumber,
    ...(window.currentPage && window.currentPage.eventName && getPageParams(window.currentPage.eventName)),
  };
};

const setWindowCustomEvent = () => {
  if (typeof window.CustomEvent !== 'function') {
    CustomEvent.prototype = window.Event.prototype;
    window.CustomEvent = CustomEvent;
  }
};

const dispatchEvent = (eventName, data) => {
  setWindowCustomEvent();
  collectAmplitudeExperiments(); // set user.testname for current page and event

  const isPage = checkArrayFilter(filter.pages, eventName);
  const isFirstLoad = checkFirstLoad();
  const eventId = setPrefix(eventName);
  const params = getParams(eventName, data);
  console.debug(`Tealium [Event]: ${eventId}`);

  if (!isFirstLoad) {
    document.dispatchEvent(new window.CustomEvent(eventId, { detail: params }));
    return;
  }
  window.tealiumFirstLoad = false;
  if (!isPage) {
    document.dispatchEvent(new window.CustomEvent(eventId, { detail: params }));
  }
};

const track = (trackCallback) => {
  if (typeof trackCallback !== 'function') return;

  if (window.utag) {
    trackCallback();
  } else {
    let retry = 0;
    const tealiumTrack = setInterval(() => {
      if (retry >= TRACK_MAX_RETRIES) {
        console.debug(`Tealium max retries (${TRACK_MAX_RETRIES}) reached. Canceling..`);
        clearInterval(tealiumTrack);
      } else if (window.utag) {
        clearInterval(tealiumTrack);
        trackCallback();
      }
      retry += 1;
    }, TRACK_RETRY_WAIT_TIME);
  }
};

const shouldTrackPage = (eventName, pathName) => {
  let shouldTrackPage = true;

  const isPageEvent = checkArrayFilter(filter.pages, eventName);
  const isSimulatedPage = checkArrayFilter(filter.simulatedPages, eventName);

  if (!window.initViewComplete) shouldTrackPage = false;
  if (!isPageEvent) shouldTrackPage = false;
  if (window.currentPage && window.currentPage.pathName === pathName && !isSimulatedPage) shouldTrackPage = false;
  return shouldTrackPage;
};

/* Public functions */

export async function loadTealium(productEnv) {
  window.tealiumFirstLoad = true;
  setUtagData();

  ((d, s, pe, v) => {
    const js = d.createElement(s);
    js.src = `//tags.tiqcdn.com/utag/${pe}/utag.js`;
    js.type = `text/java${s}`;
    js.async = true;
    js.onload = v;
    const tjs = d.getElementsByTagName(s)[0];
    tjs.parentNode.insertBefore(js, tjs);
  })(document, 'script', productEnv, tealiumViewInit);
}

export const trackEvent = (name, data) => {
  const eventName = TEALIUM_EVENTS[name];
  if (!eventName) return;

  track(() => dispatchEvent(eventName, data));
};

export const trackPage = (name, pathName, eventName) => {
  if (!shouldTrackPage(eventName, pathName)) return;
  window.currentPage = { name, pathName, eventName };
  setUtagData();
  console.debug(`Tealium [Page]: ${window.location.pathname}`);
  const eventId = setPrefix(eventName);
  document.dispatchEvent(new window.CustomEvent(eventId, { detail: window.utag_data }));
};
